/**
 * PromptInput Component
 *
 * Text area for the main prompt, plus the shared AI-image prompt options
 * (suggested quality toggle + collapsible negative prompt) via
 * AIImagePromptOptions. In the image tab the suggested-quality toggle mutates
 * the user's typed prompt inline (appends/strips the quality boosters).
 */

import { memo, useCallback, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { SUGGESTED_POSITIVE_PROMPT, SUGGESTED_NEGATIVE_PROMPT } from '../../constants/imagePrompts';
import { AIImagePromptOptions } from './AIImagePromptOptions';

interface PromptInputProps {
  prompt: string;
  negativePrompt: string;
  onPromptChange: (value: string) => void;
  onNegativePromptChange: (value: string) => void;
  disabled?: boolean;
  /** Hide negative prompt section entirely (for providers that don't support it) */
  showNegativePrompt?: boolean;
}

export const PromptInput = memo(function PromptInput({
  prompt,
  negativePrompt,
  onPromptChange,
  onNegativePromptChange,
  disabled = false,
  showNegativePrompt = true,
}: PromptInputProps) {
  const { t } = useTranslation();
  const [useSuggested, setUseSuggested] = useState(false);

  // Toggle the suggested quality prompts on/off. Reversible: enabling appends
  // the quality boosters to the prompt and fills an empty negative prompt;
  // disabling strips exactly what we added without touching the user's text.
  const handleToggleSuggested = useCallback(
    (checked: boolean) => {
      setUseSuggested(checked);
      if (checked) {
        const trimmed = prompt.trim();
        if (!trimmed.includes(SUGGESTED_POSITIVE_PROMPT)) {
          onPromptChange(
            trimmed ? `${trimmed}, ${SUGGESTED_POSITIVE_PROMPT}` : SUGGESTED_POSITIVE_PROMPT
          );
        }
        if (!negativePrompt.trim()) {
          onNegativePromptChange(SUGGESTED_NEGATIVE_PROMPT);
        }
      } else {
        onPromptChange(
          prompt
            .replace(`, ${SUGGESTED_POSITIVE_PROMPT}`, '')
            .replace(SUGGESTED_POSITIVE_PROMPT, '')
            .trim()
        );
        if (negativePrompt.trim() === SUGGESTED_NEGATIVE_PROMPT) {
          onNegativePromptChange('');
        }
      }
    },
    [prompt, negativePrompt, onPromptChange, onNegativePromptChange]
  );

  return (
    <div className="space-y-4">
      {/* Main Prompt */}
      <div className="form-group">
        <label htmlFor="prompt" className="form-label">
          {t('image.prompt.label', { defaultValue: 'Prompt' })} *
        </label>
        <textarea
          id="prompt"
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={t('image.prompt.placeholder', {
            defaultValue: 'Describe the image you want to create...',
          })}
          className="input min-h-[120px] resize-y"
          disabled={disabled}
          data-testid="image-prompt-input"
        />
      </div>

      {/* Suggested quality + negative prompt (shared with the video tab) */}
      {showNegativePrompt && (
        <AIImagePromptOptions
          negativePrompt={negativePrompt}
          onNegativePromptChange={onNegativePromptChange}
          useSuggestedQuality={useSuggested}
          onUseSuggestedQualityChange={handleToggleSuggested}
          disabled={disabled}
        />
      )}
    </div>
  );
});

export default PromptInput;
